/**
 * AproveitamentoPanel - Painel "Aproveitamento & Perdas" (visão do proprietário)
 *
 * Renderizado dentro do Relatório de Equipe. 100% leitura — não altera nenhuma
 * métrica existente nem a avaliação dos sangradores.
 *
 * Consome:
 *  - GET /api/aproveitamento/{property_id}?month=YYYY-MM
 *  - GET/PUT /api/aproveitamento/config/{property_id}
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { SafraFechamento } from "./SafraFechamento";
import { CalcPopover } from "./CalcPopover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtKg = (v) => (v == null ? "—" : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`);
const fmtRs = (v) => (v == null ? null : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const CausaRow = ({ emoji, label, dias, kg, rs, color, testId, calc }) => {
  if (!dias) return null;
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-2 text-sm py-1" data-testid={testId}>
      <span className="text-gray-700 min-w-0">{emoji} {label}{calc}</span>
      <span className="font-medium text-right ml-auto" style={{ color }}>
        <span className="whitespace-nowrap">{dias} dia(s){kg != null && <> ≈ {fmtKg(kg)}</>}</span>
        {rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(rs)})</span>}
      </span>
    </div>
  );
};

const ConfigDialog = ({ open, onOpenChange, propertyId, config, onSaved }) => {
  const [form, setForm] = useState(config || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(config || {}); }, [config]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        trabalha_domingos: !!form.trabalha_domingos,
        compensa_domingos: !!form.compensa_domingos,
        trabalha_feriados: !!form.trabalha_feriados,
        chuva_minima_mm: parseFloat(form.chuva_minima_mm) || 1.0,
        modo: form.modo || "todos",
        preco_kg: form.preco_kg ? parseFloat(form.preco_kg) : 0,
      };
      await axios.put(`${API}/aproveitamento/config/${propertyId}`, payload);
      toast.success("Configuração salva");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar configuração");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="aproveitamento-config-dialog">
        <DialogHeader>
          <DialogTitle>⚙️ Configurar Aproveitamento</DialogTitle>
          <DialogDescription>Regras da propriedade para contar dias perdidos</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Domingos</Label>
            <div className="space-y-1 mt-1 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="dom" checked={!form.trabalha_domingos && !form.compensa_domingos}
                  onChange={() => setForm({ ...form, trabalha_domingos: false, compensa_domingos: false })} data-testid="cfg-dom-folga" />
                Não trabalhamos aos domingos
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="dom" checked={!form.trabalha_domingos && !!form.compensa_domingos}
                  onChange={() => setForm({ ...form, trabalha_domingos: false, compensa_domingos: true })} data-testid="cfg-dom-compensa" />
                Não trabalhamos, mas compensamos na semana
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="dom" checked={!!form.trabalha_domingos}
                  onChange={() => setForm({ ...form, trabalha_domingos: true, compensa_domingos: false })} data-testid="cfg-dom-trabalha" />
                Trabalhamos aos domingos
              </label>
            </div>
            {!form.trabalha_domingos && !!form.compensa_domingos && (
              <p className="text-[11px] text-gray-500 mt-1">Os domingos entram como produção esperada. Lotes duplos e reposições cobrem primeiro os domingos; o que sobrar abate chuva/faltas.</p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="cfg-feriados">Trabalhamos em feriados</Label>
            <Switch id="cfg-feriados" data-testid="cfg-trabalha-feriados"
              checked={!!form.trabalha_feriados}
              onCheckedChange={(v) => setForm({ ...form, trabalha_feriados: v })} />
          </div>
          <div>
            <Label>Chuva mínima que impede sangria (mm)</Label>
            <Input type="number" min="0" step="0.5" data-testid="cfg-chuva-minima"
              value={form.chuva_minima_mm ?? 1}
              onChange={(e) => setForm({ ...form, chuva_minima_mm: e.target.value })} />
          </div>
          <div>
            <Label>Modo de contagem de perdas</Label>
            <div className="space-y-1 mt-1 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="modo" checked={(form.modo || "todos") === "todos"}
                  onChange={() => setForm({ ...form, modo: "todos" })} data-testid="cfg-modo-todos" />
                Todos os dias não sangrados
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="modo" checked={form.modo === "registrados"}
                  onChange={() => setForm({ ...form, modo: "registrados" })} data-testid="cfg-modo-registrados" />
                Só chuva/faltas registradas
              </label>
            </div>
          </div>
          <div>
            <Label>Preço do kg do coágulo (R$) — opcional</Label>
            <Input type="number" min="0" step="0.10" placeholder="Ex: 4,00" data-testid="cfg-preco-kg"
              value={form.preco_kg ?? ""}
              onChange={(e) => setForm({ ...form, preco_kg: e.target.value })} />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="cfg-salvar-btn">
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AproveitamentoPanel = ({ property, month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showFaltas, setShowFaltas] = useState(false);

  const load = useCallback(async () => {
    if (!property?.id || !month) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/aproveitamento/${property.id}`, { params: { month } });
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.detail || e.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [property?.id, month]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="bg-white p-4 rounded-lg border text-sm text-gray-500 mt-4">Carregando aproveitamento...</div>;
  }
  if (!data) {
    return (
      <div className="bg-white p-4 rounded-lg border mt-4" data-testid="aproveitamento-error">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">📉 Aproveitamento &amp; Perdas — não foi possível carregar{error ? ` (${error})` : ""}.</span>
          <Button variant="outline" size="sm" onClick={load} data-testid="aproveitamento-retry-btn">Tentar novamente</Button>
        </div>
      </div>
    );
  }

  const { dias, perdas, gap_alocacao, aproveitamento_pct, kg_corte_dia, rs_corte_dia, faltas_detalhe, avisos, dias_duplos, dias_extras, recuperado_detalhe, config, gpc_umido, plantas_lote, num_lotes, alocados_sem_producao } = data;
  const pctColor = aproveitamento_pct >= 85 ? "#16a34a" : aproveitamento_pct >= 70 ? "#d97706" : "#dc2626";
  const faltasComImpacto = (faltas_detalhe || []).filter((f) => f.kg != null);
  const fmtDias = (v) => Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

  const recPartes = [];
  if (recuperado_detalhe?.reposicoes > 0) recPartes.push(`${fmtDias(recuperado_detalhe.reposicoes)} reposição(ões)`);
  if (recuperado_detalhe?.lote_duplo > 0) recPartes.push(`${recuperado_detalhe.lote_duplo} lote(s) duplo(s)`);
  if (recuperado_detalhe?.dias_extras > 0) recPartes.push(`${fmtDias(recuperado_detalhe.dias_extras)} domingo/feriado trabalhado(s)`);
  const usadoDomingos = recuperado_detalhe?.usado_domingos || 0;

  const fmtG = (v) => Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
  const temCalc = gpc_umido && plantas_lote && kg_corte_dia;
  const calcDia = temCalc ? `Nesta propriedade: ${plantas_lote.toLocaleString("pt-BR")} plantas do lote × ${fmtG(gpc_umido)}g (g/planta/corte do mês) = ${fmtKg(kg_corte_dia)} por dia parado` : null;
  const calcCausa = (diasN, kg) => temCalc && diasN > 0 ? [
    "📖 Fórmula: dias perdidos × plantas do lote × g/planta/corte do mês.",
    "Cada dia perdido = 1 lote do rodízio sem corte. Plantas do lote = total de plantas ÷ nº de lotes do seu sistema (ex.: D/4 = 4 lotes).",
    calcDia,
    `= ${fmtDias(diasN)} dia(s) × ${fmtKg(kg_corte_dia)} = ${fmtKg(kg)}`,
  ] : null;

  return (
    <div className="bg-white p-4 rounded-lg border mt-4" data-testid="aproveitamento-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">📉 Aproveitamento &amp; Perdas</h3>
        <div className="flex items-center gap-2">
          <span
            className="text-lg font-bold cursor-help"
            style={{ color: pctColor }}
            data-testid="aproveitamento-pct"
            title="Aproveitamento = lotes sangrados + créditos de recuperação (reposições, lote duplo, trabalho em folga) ÷ dias úteis possíveis. Mede o uso da propriedade e pode passar de 100% em meses de sobre-desempenho. É diferente do 'Cortes' do Relatório de Equipe, que conta 1 corte por dia de sangria."
          >
            {aproveitamento_pct}%
          </span>
          <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="aproveitamento-config-btn">⚙️</Button>
        </div>
      </div>

      {data.fim_sangria && data.month >= data.fim_sangria.slice(0, 7) && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="fim-sangria-aviso">
          🏁 Sangria da safra encerrada em {data.fim_sangria.slice(8)}/{data.fim_sangria.slice(5, 7)}/{data.fim_sangria.slice(0, 4)} — dias após essa data não contam no aproveitamento.
        </p>
      )}

      {/* Resumo do mês */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-center">
        <div className="p-2 bg-gray-50 rounded" data-testid="aprov-dias-possiveis">
          <div className="text-lg font-bold text-gray-700">{dias.possiveis}</div>
          <div className="text-[11px] text-gray-500">Dias possíveis</div>
        </div>
        <div className="p-2 bg-emerald-50 rounded" data-testid="aprov-dias-sangrados">
          <div className="text-lg font-bold text-emerald-600">{dias.com_sangria}</div>
          <div className="text-[11px] text-gray-500">Sangrados{dias.extras_trabalhados > 0 ? ` (${dias.extras_trabalhados} em folga)` : ""}</div>
        </div>
        <div className="p-2 bg-blue-50 rounded" data-testid="aprov-recuperados">
          <div className="text-lg font-bold text-blue-600">+{fmtDias(perdas.recuperado.dias)}</div>
          <div className="text-[11px] text-gray-500">Recuperados</div>
        </div>
        <div className="p-2 bg-red-50 rounded" data-testid="aprov-perdidos">
          <div className="text-lg font-bold text-red-600">{fmtDias(perdas.liquida.dias)}</div>
          <div className="text-[11px] text-gray-500">Perdidos (líq.)</div>
        </div>
      </div>

      {/* Cascata de causas */}
      <div className="border rounded-lg px-3 py-2 mb-3 divide-y divide-gray-100">
        <CausaRow emoji="🌧️" label="Chuva (sem nenhuma atividade)" dias={perdas.chuva.dias} kg={perdas.chuva.kg} rs={perdas.chuva.rs} color="#2563eb" testId="perda-chuva"
          calc={calcCausa(perdas.chuva.dias, perdas.chuva.kg) && <CalcPopover title="Perda por chuva" lines={calcCausa(perdas.chuva.dias, perdas.chuva.kg)} testId="calc-chuva" />} />
        <CausaRow emoji="👷" label="Faltas não compensadas" dias={fmtDias(perdas.falta.dias)} kg={perdas.falta.kg} rs={perdas.falta.rs} color="#d97706" testId="perda-falta"
          calc={temCalc && perdas.falta.dias > 0 ? <CalcPopover title="Perda por faltas" lines={[
            "📖 Fórmula: cota do faltante × lotes sangrados no dia × g/planta/corte (cota = plantas alocadas dele ÷ nº de lotes do sistema, ex.: 3.000 ÷ 4 = 750).",
            "Regras: dia parado sem outra causa → cota vai p/ Faltas e o restante p/ Sem causa; dia de chuva ou outra atividade = 0; compensada = 0.",
            calcDia,
            `= ${fmtDias(perdas.falta.dias)} dia(s)-lote × ${fmtKg(kg_corte_dia)} = ${fmtKg(perdas.falta.kg)}`,
          ]} testId="calc-falta" /> : null} />
        <CausaRow emoji="❓" label="Sem causa registrada" dias={fmtDias(perdas.sem_causa.dias)} kg={perdas.sem_causa.kg} rs={perdas.sem_causa.rs} color="#6b7280" testId="perda-sem-causa"
          calc={calcCausa(perdas.sem_causa.dias, perdas.sem_causa.kg) && <CalcPopover title="Perda sem causa registrada" lines={calcCausa(perdas.sem_causa.dias, perdas.sem_causa.kg)} testId="calc-sem-causa" />} />
        {perdas.domingo_nao_compensado?.dias > 0 && (
          <CausaRow emoji="📅" label="Domingos não compensados" dias={fmtDias(perdas.domingo_nao_compensado.dias)} kg={perdas.domingo_nao_compensado.kg} rs={perdas.domingo_nao_compensado.rs} color="#9333ea" testId="perda-domingo"
            calc={<CalcPopover title="Domingos não compensados" lines={[
              "📖 Fórmula: (domingos do mês − compensados por créditos) × plantas do lote × g/planta/corte.",
              "Config ativa: compensamos os domingos na semana — os créditos pagam PRIMEIRO os domingos.",
              `Nesta propriedade: ${dias.domingos} domingo(s) − ${fmtDias(recuperado_detalhe?.usado_domingos || 0)} compensado(s) = ${fmtDias(perdas.domingo_nao_compensado.dias)} sem compensação.`,
              `= ${fmtDias(perdas.domingo_nao_compensado.dias)} domingo(s) × ${kg_corte_dia ? fmtKg(kg_corte_dia) : "kg/dia"} = ${perdas.domingo_nao_compensado.kg != null ? fmtKg(perdas.domingo_nao_compensado.kg) : "—"}`,
            ]} testId="calc-domingo" />} />
        )}
        {(perdas.recuperado.dias > 0 || usadoDomingos > 0) && (
          <div className="flex flex-wrap items-start justify-between gap-x-2 text-sm py-1" data-testid="perda-recuperado">
            <span className="text-gray-700 min-w-0">♻️ Recuperado{recPartes.length > 0 ? ` (${recPartes.join(" + ")}${usadoDomingos > 0 ? ` − ${fmtDias(usadoDomingos)} usado(s) nos domingos` : ""})` : ""}
              <CalcPopover title="Créditos de recuperação" lines={[
                "📖 Fórmula: reposições + lotes duplos (2º lote no dia) + sangrias em dia de folga − créditos usados nos domingos.",
                "Cada crédito = 1 corte de lote e abate 1 dia da perda bruta. Se a config de compensar domingos estiver ativa, os créditos pagam PRIMEIRO os domingos.",
                `Nesta propriedade: ${recPartes.join(" + ") || "créditos"}${usadoDomingos > 0 ? ` − ${fmtDias(usadoDomingos)} usado(s) nos domingos` : ""}.`,
                `= +${fmtDias(perdas.recuperado.dias)} corte(s) abatendo a perda bruta.`,
              ]} testId="calc-recuperado" />
            </span>
            <span className="font-medium text-emerald-600 text-right ml-auto whitespace-nowrap">+{fmtDias(perdas.recuperado.dias)} corte(s){perdas.recuperado.kg != null && <> ≈ {fmtKg(perdas.recuperado.kg)}</>}</span>
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-x-2 text-sm py-1.5 font-semibold" data-testid="perda-liquida">
          <span className="text-gray-800 min-w-0">Perda líquida do mês</span>
          <span className="text-red-600 text-right ml-auto">
            <span className="whitespace-nowrap">{fmtDias(perdas.liquida.dias)} dia(s){perdas.liquida.kg != null && <> ≈ {fmtKg(perdas.liquida.kg)}</>}</span>
            {perdas.liquida.rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(perdas.liquida.rs)})</span>}
          </span>
        </div>
      </div>

      {/* Gap de alocação */}
      {gap_alocacao?.dias_com_deficit > 0 && (
        <div className="border border-purple-200 bg-purple-50 rounded-lg px-3 py-2 mb-3 text-sm" data-testid="gap-alocacao">
          <div className="flex flex-wrap items-start justify-between gap-x-2">
            <span className="text-purple-800 min-w-0">🚫 Plantas sem trabalhador alocado
              {gap_alocacao.kg != null && gap_alocacao.cortes_perdidos_planta != null && (
                <CalcPopover title="Plantas sem dono" lines={[
                  "📖 Fórmula (vale para qualquer propriedade): plantas sem dono × cortes perdidos por planta × g/planta/corte do mês.",
                  "Cortes perdidos por planta = cortes reais que os lotes receberam no mês × (dias descobertos ÷ dias de trabalho). Usa o ritmo REAL do mês (já inclui reposições) — nunca acima do que a equipe produziu.",
                  "No rodízio ninguém corta a alocação inteira num dia (~1 lote por dia + reposições) — por isso a conta é em cortes POR PLANTA, não plantas por dia.",
                  `Nesta propriedade: ~${Number(gap_alocacao.cortes_mes).toLocaleString("pt-BR")} corte(s) no mês × (${gap_alocacao.dias_com_deficit} ÷ ${gap_alocacao.dias_trabalho} dias) = ${Number(gap_alocacao.cortes_perdidos_planta).toLocaleString("pt-BR")} corte(s) perdido(s) por planta.`,
                  `= ${gap_alocacao.plantas_deficit.toLocaleString("pt-BR")} plantas × ${Number(gap_alocacao.cortes_perdidos_planta).toLocaleString("pt-BR")} corte(s) × ${fmtG(gpc_umido)}g/corte = ${fmtKg(gap_alocacao.kg)}`,
                ]} testId="calc-gap" />
              )}
            </span>
            <span className="font-medium text-purple-700 text-right ml-auto">
              {gap_alocacao.kg != null && <span className="whitespace-nowrap">{fmtKg(gap_alocacao.kg)}</span>}{gap_alocacao.rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(gap_alocacao.rs)})</span>}
            </span>
          </div>
          {gap_alocacao.janelas?.map((j, i) => (
            <p key={i} className="text-[11px] text-purple-600 mt-1">
              {j.plantas.toLocaleString("pt-BR")} plantas sem dono em {j.dias} dia(s) de trabalho ({j.de.slice(8)}/{j.de.slice(5, 7)} a {j.ate.slice(8)}/{j.ate.slice(5, 7)}) — configure vigências em Configurar Safra
            </p>
          ))}
        </div>
      )}

      {/* Alocados sem produção no mês */}
      {alocados_sem_producao?.length > 0 && (
        <div className="border border-orange-200 bg-orange-50 rounded-lg px-3 py-2 mb-3 text-sm" data-testid="alocado-sem-producao">
          {alocados_sem_producao.map((w, i) => (
            <div key={i} className="flex flex-wrap items-start justify-between gap-x-2">
              <span className="text-orange-800 min-w-0">⚠️ {w.nome} está alocado mas sem produção registrada
                <CalcPopover title="Alocado sem produção" lines={[
                  "📖 Indicação: este trabalhador tem vigência ativa e plantas alocadas, mas nenhuma entrega registrada no nome dele neste mês.",
                  "Pode ser: sangrador em treinamento (sem produção aproveitável), entrega registrada no nome de outro, ou esquecimento de registro. Se ele não trabalhou, ajuste a vigência em Configurar Safra.",
                  "📖 Estimativa do rendimento da tarefa dele: plantas × dias ativos × rendimento por planta-dia de quem produziu no mês.",
                  "Planta-dia = planta SOB RESPONSABILIDADE no dia (não cortada no dia). O rodízio já está embutido no rendimento — dá o MESMO resultado que contar as plantas cortadas por dia (ex.: 750 no D/4) × g/planta/corte.",
                  `Nesta propriedade: ${w.plantas.toLocaleString("pt-BR")} plantas ativas em ${w.dias_ativos} de ${w.dias_trabalho} dia(s) de trabalho ⇒ ≈ ${fmtKg(w.kg_estimado)}`,
                ]} testId={`calc-alocado-sem-prod-${i}`} />
              </span>
              <span className="font-medium text-orange-700 text-right ml-auto">
                <span className="whitespace-nowrap">≈ {fmtKg(w.kg_estimado)}</span>{w.rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(w.rs)})</span>}
              </span>
            </div>
          ))}
          <p className="text-[11px] text-orange-600 mt-1">Não entra nas perdas — é um alerta para confirmar a vigência ou o registro das entregas.</p>
        </div>
      )}

      {/* Custo do dia parado */}
      {kg_corte_dia != null && (
        <div className="flex flex-wrap items-start justify-between gap-x-2 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 mb-3 text-sm" data-testid="custo-dia-parado">
          <span className="text-amber-800 min-w-0">☔ 1 dia sem sangria nesta propriedade
            {temCalc && (
              <CalcPopover title="Custo de 1 dia parado" lines={[
                "📖 Fórmula: plantas do lote × g/planta/corte do mês (plantas do lote = total de plantas ÷ nº de lotes do sistema).",
                `= ${calcDia}`,
              ]} testId="calc-dia-parado" />
            )}
          </span>
          <span className="font-bold text-amber-700 text-right ml-auto">
            <span className="whitespace-nowrap">≈ {fmtKg(kg_corte_dia)}</span>{rs_corte_dia != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(rs_corte_dia)})</span>}
          </span>
        </div>
      )}

      {/* Faltas individuais */}
      {faltasComImpacto.length > 0 && (
        <div className="mb-3">
          <button className="text-xs text-blue-600 underline" onClick={() => setShowFaltas(!showFaltas)} data-testid="toggle-faltas-detalhe">
            {showFaltas ? "Ocultar" : "Ver"} impacto por falta individual ({faltasComImpacto.length})
          </button>
          {showFaltas && (
            <div className="mt-2 space-y-1">
              {faltasComImpacto.map((f, i) => (
                <div key={i} className="flex justify-between text-xs bg-gray-50 rounded px-2 py-1" data-testid={`falta-item-${i}`}>
                  <span>{f.date.slice(8)}/{f.date.slice(5, 7)} — {f.trabalhador}{f.motivo ? ` (${f.motivo})` : ""}
                    {f.calc && <CalcPopover title={`Falta de ${f.trabalhador} em ${f.date.slice(8)}/${f.date.slice(5, 7)}`} lines={[f.calc]} testId={`calc-falta-item-${i}`} />}
                  </span>
                  <span className={f.compensada ? "text-emerald-600" : "text-red-600"}>
                    {f.compensada ? "✓ compensada" : `−${fmtKg(f.kg)}${f.rs ? ` (${fmtRs(f.rs)})` : ""}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Avisos */}
      {avisos?.length > 0 && (
        <div className="text-[11px] text-gray-500 space-y-0.5" data-testid="aproveitamento-avisos">
          {avisos.map((a, i) => <p key={i}>⚠️ {a}</p>)}
        </div>
      )}
      {/* Fechamento de Safra + Simulador (Fase 2) */}
      <SafraFechamento property={property} month={month} />

      <p className="text-[11px] text-gray-400 mt-2">
        📐 Estimativas baseadas nos seus registros ({config.modo === "todos" ? "todos os dias não sangrados" : "só causas registradas"}, chuva ≥ {config.chuva_minima_mm}mm{config.trabalha_domingos ? ", inclui domingos" : config.compensa_domingos ? ", domingos compensados na semana" : ", exclui domingos"}{config.trabalha_feriados ? ", inclui feriados" : ", exclui feriados"}). Não afeta a avaliação dos sangradores.
      </p>

      <ConfigDialog open={showConfig} onOpenChange={setShowConfig} propertyId={property.id} config={config} onSaved={load} />
    </div>
  );
};
